import { useEffect, useState } from 'react';
import { Plus, Trash2, Check, Lock, Undo2, CopyPlus, Save } from 'lucide-react';
import { api } from '../../lib/api';
import Modal from '../ui/Modal';
import {
  DEAL_ROLES, toForm, fromForm, describeTerms, inputCls, btnCls, btn2Cls, cardCls, fmtDate,
} from './shared';

// The terms of one version. A draft is editable by the agreement's partners and the owner;
// any change (terms or comments) clears every agree mark. Each partner marks agree for themselves; the owner
// locks once everyone has. A locked version is read-only; changes start a new draft from it.

export default function TermsTab({ detail, version, onChanged, onDirtyChange = () => {} }) {
  const partners = detail.partners;
  const [form, setForm] = useState(() => toForm(version.terms));
  const [comments, setComments] = useState(version.comments || '');
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [confirmLock, setConfirmLock] = useState(false);

  useEffect(() => { setForm(toForm(version.terms)); setComments(version.comments || ''); setDirty(false); setError(''); }, [version.id, version.updated_at]);
  const unsaved = dirty || comments !== (version.comments || '');
  useEffect(() => { onDirtyChange(unsaved); }, [unsaved]);

  const edit = (fn) => { setForm((f) => { const n = structuredClone(f); fn(n); return n; }); setDirty(true); };
  const base = `/partner-comp/agreements/${detail.agreement.id}/versions/${version.id}`;
  const run = async (fn) => {
    setBusy(true); setError('');
    try { await fn(); await onChanged(); } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const save = () => run(async () => {
    const { terms, errors } = fromForm(form);
    if (errors.length) throw new Error(errors.join('; '));
    await api.patch(base, { terms, comments, revision: version.updated_at });
    setDirty(false);
  });
  const saveComments = () => run(() => api.patch(base, { comments, revision: version.updated_at }));
  const agree = () => run(() => api.post(`${base}/agree`, { terms_revision: version.terms_revision }));
  const withdraw = () => run(() => api.delete(`${base}/agree`));
  const lock = () => run(async () => { await api.post(`${base}/lock`, { terms_revision: version.terms_revision }); setConfirmLock(false); });
  const newDraft = () => run(() => api.post(`/partner-comp/agreements/${detail.agreement.id}/drafts`, { from_version_id: version.id }));

  const editable = version.can_edit;
  const hasDraft = detail.versions.some((v) => v.status === 'draft');
  const summary = describeTerms(version.terms);
  const paidToOptions = [
    ...DEAL_ROLES.map((d) => ({ value: `deal:${d.key}`, label: `Whoever the deal assigns as ${d.label}` })),
    ...form.roles.filter((r) => r.paidTo.startsWith('deal:') && !DEAL_ROLES.some((d) => `deal:${d.key}` === r.paidTo)).map((r) => ({ value: r.paidTo, label: `Deal role ${r.paidTo.slice(5)}` })),
    ...partners.map((p) => ({ value: `partner:${p.partner}`, label: p.partner })),
  ];

  return (
    <div className="space-y-4">
      <StatusBanner version={version} partners={partners} />
      {error && <p className="rounded-lg bg-rose-50 p-2 text-sm text-rose-700 dark:bg-rose-900/20 dark:text-rose-300">{error}</p>}

      <div className="flex flex-wrap gap-2">
        {version.can_agree && !dirty && comments === (version.comments || '') && <button className={btnCls} disabled={busy} onClick={agree}><Check size={15} /> I agree to these terms</button>}
        {version.can_withdraw && <button className={btn2Cls} disabled={busy} onClick={withdraw}><Undo2 size={15} /> Withdraw my agreement</button>}
        {version.can_lock && !dirty && comments === (version.comments || '') && <button className={btnCls} disabled={busy} onClick={() => setConfirmLock(true)}><Lock size={15} /> Lock this version</button>}
        {version.status === 'locked' && !hasDraft && <button className={btn2Cls} disabled={busy} onClick={newDraft}><CopyPlus size={15} /> Start a new draft from this version</button>}
        {editable && dirty && <button className={btnCls} disabled={busy} onClick={save}><Save size={15} /> Save draft (clears every agree mark)</button>}
      </div>
      {version.terms_error && <p className="text-sm text-rose-600">These terms need fixing before anyone can agree: {version.terms_error}</p>}

      {!editable ? (
        <div className={cardCls}>
          <h3 className="mb-2 font-semibold text-[var(--text-primary)]">Terms of version {version.version_no}</h3>
          <ul className="list-disc space-y-1 pl-5 text-sm text-[var(--text-primary)]">
            <li>{summary.order}</li>
            {summary.roles.length ? summary.roles.map((r) => <li key={r}>{r}</li>) : <li>No role fees.</li>}
            <li>The rest is shared by ownership: {summary.ownership}.</li>
            {summary.paybacks.length > 0 && <li>Paybacks repaid from deal cash ({version.terms.paybacks.split === 'in_order' ? 'in list order' : 'shared by what each is owed'}): {summary.paybacks.join('; ')}.</li>}
            {summary.overhead && <li>{summary.overhead}, carried while unpaid.</li>}
            <li>No role fees when the fee base is zero; a loss is shared by ownership.{version.terms.flag_dual_role ? ' A partner holding two deal roles is flagged for the other partners\' approval.' : ''}</li>
          </ul>
          {version.comments && <p className="mt-2 text-sm text-[var(--text-secondary)]">Comments: {version.comments}</p>}
        </div>
      ) : (
        <div className="space-y-4">
          <section className={cardCls}>
            <h3 className="mb-2 font-semibold text-[var(--text-primary)]">Order</h3>
            <div className="flex flex-col gap-1 text-sm sm:flex-row sm:gap-4">
              {[['fees_first', 'Role fees first, on net proceeds; paybacks and overhead from the Company share'], ['waterfall', 'Paybacks and overhead off the top; role fees on what is left']].map(([k, l]) => (
                <label key={k} className="flex items-start gap-2"><input type="radio" name="order" checked={form.order === k} onChange={() => edit((f) => { f.order = k; })} /> {l}</label>
              ))}
            </div>
          </section>

          <section className={cardCls}>
            <h3 className="mb-2 font-semibold text-[var(--text-primary)]">Role fees</h3>
            {form.roles.length === 0 && <p className="mb-2 text-sm text-[var(--text-secondary)]">No role fees: everything after paybacks and overhead is shared by ownership.</p>}
            <div className="space-y-3">
              {form.roles.map((r, i) => (
                <div key={i} className="rounded-lg border border-[var(--border)] p-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <input className={`${inputCls} w-44`} value={r.label} placeholder="Role name" aria-label="Role name" onChange={(e) => edit((f) => { f.roles[i].label = e.target.value; })} />
                    <span className="text-sm text-[var(--text-secondary)]">paid to</span>
                    <select className={inputCls} value={r.paidTo} aria-label="Paid to" onChange={(e) => edit((f) => { f.roles[i].paidTo = e.target.value; })}>
                      {paidToOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    <button className="ml-auto text-rose-600" aria-label="Remove role" onClick={() => edit((f) => { f.roles.splice(i, 1); })}><Trash2 size={15} /></button>
                  </div>
                  <div className="mt-2 space-y-1">
                    {r.tiers.map((x, j) => {
                      const last = j === r.tiers.length - 1;
                      return (
                        <div key={j} className="flex flex-wrap items-center gap-2 text-sm">
                          <input className={`${inputCls} w-20 text-right`} value={x.pct} aria-label="Percent" onChange={(e) => edit((f) => { f.roles[i].tiers[j].pct = e.target.value; })} />
                          <span>%</span>
                          {last ? <span className="text-[var(--text-secondary)]">{r.tiers.length > 1 ? 'of the rest of the fee base' : 'of the fee base'}</span> : (
                            <>
                              <span className="text-[var(--text-secondary)]">of the fee base up to $</span>
                              <input className={`${inputCls} w-32 text-right`} value={x.upTo} aria-label="Up to" onChange={(e) => edit((f) => { f.roles[i].tiers[j].upTo = e.target.value; })} />
                            </>
                          )}
                          {r.tiers.length > 1 && <button className="text-rose-600" aria-label="Remove rate" onClick={() => edit((f) => { f.roles[i].tiers.splice(j, 1); })}><Trash2 size={13} /></button>}
                        </div>
                      );
                    })}
                    {r.tiers.length < 5 && <button className="text-xs text-primary" onClick={() => edit((f) => { f.roles[i].tiers.splice(f.roles[i].tiers.length - 1, 0, { upTo: '', pct: f.roles[i].tiers[f.roles[i].tiers.length - 1].pct }); })}>+ Add a rate step (different % up to an amount)</button>}
                  </div>
                </div>
              ))}
            </div>
            {form.roles.length < 8 && <button className={`${btn2Cls} mt-2`} onClick={() => edit((f) => { f.roles.push({ key: '', label: 'New role', paidTo: `partner:${partners[0]?.partner}`, tiers: [{ upTo: '', pct: '10' }] }); })}><Plus size={14} /> Add a role</button>}
            <label className="mt-3 flex items-center gap-2 text-sm"><input type="checkbox" checked={form.flag_dual_role} onChange={(e) => edit((f) => { f.flag_dual_role = e.target.checked; })} /> Flag a partner who holds two deal roles for the other partners' approval</label>
          </section>

          <section className={cardCls}>
            <h3 className="mb-2 font-semibold text-[var(--text-primary)]">Ownership (shares what is left)</h3>
            <div className="flex flex-wrap gap-3">
              {form.ownership.map((o, i) => (
                <label key={o.partner} className="flex items-center gap-1 text-sm">{o.partner}
                  <input className={`${inputCls} w-20 text-right`} value={o.pct} aria-label={`${o.partner} ownership percent`} onChange={(e) => edit((f) => { f.ownership[i].pct = e.target.value; })} />%
                </label>
              ))}
            </div>
          </section>

          <section className={cardCls}>
            <h3 className="mb-2 font-semibold text-[var(--text-primary)]">Paybacks (money fronted, repaid from deal cash)</h3>
            <select className={`${inputCls} mb-2`} value={form.paybacks.split} aria-label="Payback order" onChange={(e) => edit((f) => { f.paybacks.split = e.target.value; })}>
              <option value="pro_rata">Shared by what each is still owed</option>
              <option value="in_order">In the order listed</option>
            </select>
            <div className="space-y-2">
              {form.paybacks.items.map((it, i) => (
                <div key={i} className="flex flex-wrap items-center gap-2 text-sm">
                  <input className={`${inputCls} w-48`} value={it.label} placeholder="e.g. Due to Chad" aria-label="Payback name" onChange={(e) => edit((f) => { f.paybacks.items[i].label = e.target.value; })} />
                  <select className={inputCls} value={it.partner} aria-label="Repaid to partner" onChange={(e) => edit((f) => { f.paybacks.items[i].partner = e.target.value; })}>
                    <option value="">Not a partner</option>
                    {partners.map((p) => <option key={p.partner} value={p.partner}>{p.partner}</option>)}
                  </select>
                  <span>$</span><input className={`${inputCls} w-32 text-right`} value={it.amount} aria-label="Amount" onChange={(e) => edit((f) => { f.paybacks.items[i].amount = e.target.value; })} />
                  <button className="text-rose-600" aria-label="Remove payback" onClick={() => edit((f) => { f.paybacks.items.splice(i, 1); })}><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
            {form.paybacks.items.length < 10 && <button className={`${btn2Cls} mt-2`} onClick={() => edit((f) => { f.paybacks.items.push({ key: '', label: '', partner: '', amount: '0.00' }); })}><Plus size={14} /> Add a payback</button>}
            <p className="mt-1 text-xs text-[var(--text-secondary)]">The amount is what was fronted in total; what earlier payments repaid carries over to later versions. Removing a payback and adding it again starts a new one.</p>
          </section>

          <section className={cardCls}>
            <h3 className="mb-2 font-semibold text-[var(--text-primary)]">Overhead at cost</h3>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <input className={`${inputCls} w-56`} value={form.overhead.label} aria-label="Overhead name" onChange={(e) => edit((f) => { f.overhead.label = e.target.value; })} />
              <span>$</span><input className={`${inputCls} w-28 text-right`} value={form.overhead.monthly} aria-label="Monthly amount" onChange={(e) => edit((f) => { f.overhead.monthly = e.target.value; })} /><span>a month, from</span>
              <input type="month" className={inputCls} value={form.overhead.start_month} aria-label="Start month" onChange={(e) => edit((f) => { f.overhead.start_month = e.target.value; })} />
            </div>
            <p className="mt-1 text-xs text-[var(--text-secondary)]">Accrues each month from the start month; what a payment cannot cover carries to the next one.</p>
          </section>

          <section className={cardCls}>
            <h3 className="mb-2 font-semibold text-[var(--text-primary)]">Comments (agreed along with the terms)</h3>
            <textarea className={`${inputCls} w-full`} rows={3} value={comments} onChange={(e) => setComments(e.target.value)} aria-label="Comments" />
            {!dirty && comments !== (version.comments || '') && <button className={`${btn2Cls} mt-2`} disabled={busy} onClick={saveComments}>Save comments (clears every agree mark)</button>}
          </section>
        </div>
      )}

      {confirmLock && (
        <Modal onClose={() => setConfirmLock(false)} title={`Lock version ${version.version_no}?`}>
          <p className="text-sm text-[var(--text-primary)]">Every partner has agreed to these terms. Locking makes this version read-only and dated today; it then applies to payments received from today until a later version is locked. Nothing is paid by locking it.</p>
          <div className="mt-4 flex justify-end gap-2">
            <button className={btn2Cls} onClick={() => setConfirmLock(false)}>Cancel</button>
            <button className={btnCls} disabled={busy} onClick={lock}><Lock size={15} /> Lock version {version.version_no}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function StatusBanner({ version, partners }) {
  if (version.status === 'locked') {
    return (
      <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-800 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300">
        <strong>Version {version.version_no} is locked</strong> (read-only) since {fmtDate(version.locked_on)} by {version.locked_by}. Agreed by {version.marks.map((m) => m.partner).join(', ')}.
        <span className="block text-xs opacity-80">Snapshot fingerprint {version.snapshot_sha256?.slice(0, 16)}...</span>
      </div>
    );
  }
  const agreed = version.marks.filter((m) => m.current).map((m) => m.partner);
  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
      <strong>Version {version.version_no} is a draft</strong> (revision {version.terms_revision}). {agreed.length ? `Agreed: ${agreed.join(', ')}.` : 'Nobody has agreed yet.'} {version.terms_error ? 'The terms need fixing before anyone can agree.' : version.pending_partners.length ? `Waiting for: ${version.pending_partners.join(', ')}.` : `All ${partners.length} partners agree; the owner can lock it.`}
    </div>
  );
}
